import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthService, permissionSlugs } from 'src/app/auth.service';
import { AUTHZ_FEATURES } from 'src/app/constants/permissions';
import { LoaderService } from 'src/app/loader.service';
import { MakerCheckerService } from 'src/app/shared/maker-checker/maker-checker.service';
import { SharedService } from 'src/app/shared/shared.service';
import { MakerCheckerViewDialogComponent } from 'src/app/shared/maker-checker/maker-checker-view-dialog.component';
import { environment } from '../../../environments/environment';
import { Pagination, Table, TableAction, TableActions, TableData, TableDataTypes } from 'src/app/shared/types';
import { Toaster, ToasterType } from 'src/app/shared/types/toaster.types';
import { ToasterService } from 'src/app/toaster.service';
import { MatDialog } from '@angular/material';

@Component({
  selector: 'app-maker-checker-approvals',
  templateUrl: './maker-checker-approvals.component.html',
  styleUrls: ['./maker-checker-approvals.component.css']
})
export class MakerCheckerApprovalsComponent implements OnInit {
  public title = 'Approvals';
  public table: Table = new Table({
    headers: { status: 'Status', actions: 'Actions' },
    pagination: new Pagination(0, 15, 0, [15, 20, 50]),
    data: []
  });
  public showFilters = true;
  public filterComponents: any[] = [];

  private entityType = '';
  private changeLabel = 'limit';
  private approvalsBase: string;
  private payloadFields: { key: string, label: string }[] = [];
  private appliedFilters: any = {};
  private allRows: any[] = [];
  private currentEmail = '';
  private canApprove = false;
  private canCreateLimit = false;
  private cityIdToName: { [id: string]: string } = {};
  private serviceIdToName: { [id: string]: string } = {};
  private servicesRequested: { [cityId: string]: boolean } = {};

  constructor(
    private route: ActivatedRoute,
    private makerChecker: MakerCheckerService,
    private sharedService: SharedService,
    private loader: LoaderService,
    private authService: AuthService,
    private dialog: MatDialog,
    public toastService: ToasterService
  ) {}

  ngOnInit() {
    const data = this.route.snapshot.data || {};
    this.entityType = data.entityType;
    this.title = data.title || (this.entityType + ' Approvals');
    this.changeLabel = data.changeLabel || 'limit';
    this.approvalsBase = data.approvalsApi ? `${environment.server}${data.approvalsApi}` : undefined;
    this.payloadFields = data.payloadFields || [];
    this.table = new Table({
      headers: this.buildHeaders(),
      pagination: new Pagination(0, 15, 0, [15, 20, 50]),
      data: []
    });
    this.authService.getUserDetails().subscribe((user: any) => {
      this.currentEmail = user && user.email ? String(user.email).toLowerCase() : '';
    });
    const panels = this.authService.getAuthzPanels();
    if (data.approvePermission) {
      this.canApprove = this.authService.isAuthorizedFor(data.approvePermission);
      this.canCreateLimit = this.authService.isAuthorizedFor(data.withdrawPermission);
    } else {
      const feature = data.feature || AUTHZ_FEATURES.LIMIT_STORE;
      this.canApprove = this.authService.isAuthorizedFor(
        permissionSlugs(feature, ['approve'], panels),
        feature
      );
      this.canCreateLimit = this.authService.isAuthorizedFor(
        permissionSlugs(feature, ['edit'], panels),
        feature
      );
    }
    this.filterComponents = [{
      type: 'radio-button',
      title: 'Status',
      name: 'approval-status',
      values: ['PENDING', 'APPROVED', 'REJECTED', 'ALL'],
      default: 'PENDING',
      key: 'approval-status'
    }];
    this.appliedFilters = { 'approval-status': 'PENDING' };
    this.fetchCities();
    this.fetchApprovals();
  }

  public applyFilters(filters: any) {
    this.appliedFilters = filters || {};
    this.table.pagination.pageNo = 0;
    this.fetchApprovals();
  }

  public renderPage() {
    this.fetchApprovals();
  }

  private readPayload(value: any): any {
    let parsed = value;
    while (typeof parsed === 'string') {
      try {
        parsed = JSON.parse(parsed);
      } catch (err) {
        return {};
      }
    }
    return parsed && typeof parsed === 'object' ? parsed : {};
  }

  private fetchCities() {
    this.sharedService.fetchCities().toPromise()
      .then((res: any) => {
        const cities = ((res && res.data && res.data.cities) || [])
          .filter((city: any) => city && city._id);
        this.cityIdToName = {};
        cities.forEach((city: any) => {
          this.cityIdToName[city._id] = city.displayName || city.name || city._id;
        });
        this.renderTable();
      })
      .catch(() => {});
  }

  private loadServiceNames() {
    const cityIds: string[] = [];
    this.allRows.forEach((row) => {
      const cityId = row.payload && row.payload.city;
      if (cityId && !this.servicesRequested[cityId] && cityIds.indexOf(cityId) < 0) {
        cityIds.push(cityId);
      }
    });
    cityIds.forEach((cityId) => {
      this.servicesRequested[cityId] = true;
      this.sharedService.cityService(cityId).toPromise()
        .then((res: any) => {
          ((res && res.data) || []).forEach((sd: any) => {
            if (sd && sd.service && sd.service._id) {
              this.serviceIdToName[sd.service._id] = sd.service.name || sd.service._id;
            }
          });
          this.renderTable();
        })
        .catch(() => {});
    });
  }

  private identifierName(row: any): string {
    const id = row && row.identifierId;
    if (!id) {
      return '--';
    }
    if (row.identifier === 'CITY') {
      return this.cityIdToName[id] || '--';
    }
    if (row.identifier === 'SERVICE') {
      return this.serviceIdToName[id] || '--';
    }
    return String(id);
  }

  private displayField(payload: any, key: string): string {
    const raw = payload || {};
    if (key === 'city') {
      if (!raw.city) {
        return '--';
      }
      return this.cityIdToName[raw.city] || String(raw.city);
    }
    if (key === 'identifierId') {
      return this.identifierName(raw);
    }
    if (key === 'warning') {
      const warn = raw.metadata && raw.metadata.negativeBalanceWarn;
      return warn == null || warn === '' ? '--' : String(warn);
    }
    if (key === 'active') {
      if (raw.active == null || raw.active === '') {
        return '--';
      }
      return raw.active ? 'Active' : 'Inactive';
    }
    if (Array.isArray(raw[key])) {
      return raw[key].length + ' rows';
    }
    const value = raw[key];
    return value == null || value === '' ? '--' : String(value);
  }

  private displayPayload(payload: any): any {
    const raw = payload || {};
    const display = Object.assign({}, raw);
    if (raw.city) {
      display.city = this.cityIdToName[raw.city] || raw.city;
    }
    if (raw.identifierId) {
      display.identifierId = this.identifierName(raw);
    }
    const warn = raw.metadata && raw.metadata.negativeBalanceWarn;
    display.warning = warn == null || warn === '' ? undefined : warn;
    return display;
  }

  private buildHeaders() {
    const headers: any = {};
    this.payloadFields.forEach((field) => {
      headers[field.key] = field.label;
    });
    headers.maker = 'Maker';
    headers.checker = 'Checker';
    headers.status = 'Status';
    headers.createdAt = 'Submitted';
    headers.actions = 'Actions';
    return headers;
  }

  private fetchApprovals() {
    if (!this.entityType) {
      return;
    }
    const status = this.appliedFilters['approval-status'] || 'PENDING';
    const pageNo = this.table.pagination.pageNo || 0;
    const perPage = this.table.pagination.perPage || 15;
    this.loader.openLoading();
    this.makerChecker.list(status, pageNo, perPage, this.approvalsBase).toPromise()
      .then((res: any) => {
        const rows = Array.isArray(res) ? res : ((res && res.data) || []);
        this.allRows = rows.map((row: any) => ({
          id: row._id,
          makerEmail: row.createdBy,
          checkerEmail: String(row.requestStatus || '').toUpperCase() === 'APPROVED' ? row.statusChangedBy : '',
          remark: row.comment,
          payload: this.readPayload(row.newValue),
          previousPayload: this.readPayload(row.oldValue),
          status: row.requestStatus,
          createdAt: row.createdOn ? new Date(row.createdOn).toISOString() : null,
          updatedAt: row.statusChangedOn ? new Date(row.statusChangedOn).toISOString() : null
        }));
        this.table.pagination.count = (res && res.count) || 0;
        this.loadServiceNames();
        this.renderTable();
      })
      .catch((error) => {
        this.allRows = [];
        this.table.pagination.count = 0;
        this.renderTable();
        const body = error && error.error;
        this.toastService.showToaster(new Toaster({
          type: ToasterType.WARNING,
          message: (body && body.message) || 'Error fetching approvals'
        }));
      })
      .finally(() => this.loader.closeLoading());
  }

  private renderTable() {
    this.table.data = this.allRows.map((row) => {
      const mapped: any = {
        maker: row.makerEmail || '--',
        checker: row.checkerEmail || '',
        status: row.status || '--',
        createdAt: row.createdAt ? new Date(row.createdAt).toLocaleString() : '--',
        actions: this.buildActions(row)
      };
      this.payloadFields.forEach((field) => {
        mapped[field.key] = this.displayField(row.payload, field.key);
      });
      return mapped;
    });
  }

  private buildActions(row: any) {
    const actionArr = [
      new TableAction({
        text: 'Review',
        onClick: () => this.reviewRequest(row)
      })
    ];
    return new TableData({
      type: TableDataTypes.ACTION,
      actions: new TableActions(actionArr)
    });
  }

  private reviewRequest(row: any) {
    const isOwnRequest = !!(row.makerEmail && this.currentEmail
      && String(row.makerEmail).toLowerCase() === this.currentEmail);
    const pending = row.status === 'PENDING';
    const checkerCanDecide = pending && this.canApprove && !isOwnRequest;
    const makerCanWithdraw = pending && isOwnRequest && this.canCreateLimit;
    this.dialog.open(MakerCheckerViewDialogComponent, {
      width: '720px',
      data: {
        title: this.title,
        status: row.status,
        makerEmail: row.makerEmail,
        checkerEmail: row.checkerEmail,
        remark: row.remark,
        createdAt: row.createdAt ? new Date(row.createdAt).toLocaleString() : '--',
        updatedAt: row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '--',
        payload: this.displayPayload(row.payload),
        previousPayload: this.displayPayload(row.previousPayload),
        payloadFields: this.payloadFields,
        canApprove: checkerCanDecide,
        canReject: checkerCanDecide || makerCanWithdraw,
        showDecision: checkerCanDecide || makerCanWithdraw,
        isOwnRequest,
        isMakerWithdraw: makerCanWithdraw && !checkerCanDecide
      }
    }).afterClosed().subscribe((result) => {
      if (!result || !result.action) {
        return;
      }
      this.decide(row, result.action === 'approve', result.remark);
    });
  }

  private decide(row: any, approve: boolean, remark?: string) {
    const isOwnRequest = !!(row.makerEmail && this.currentEmail
      && String(row.makerEmail).toLowerCase() === this.currentEmail);
    if (approve && isOwnRequest) {
      this.toastService.showToaster(new Toaster({
        type: ToasterType.WARNING,
        message: 'Maker cannot approve their own request'
      }));
      return;
    }
    if (approve && !this.canApprove) {
      this.toastService.showToaster(new Toaster({
        type: ToasterType.WARNING,
        message: `You do not have permission to approve ${this.changeLabel} changes`
      }));
      return;
    }
    if (!approve && isOwnRequest && !this.canCreateLimit) {
      this.toastService.showToaster(new Toaster({
        type: ToasterType.WARNING,
        message: 'You do not have permission to withdraw this request'
      }));
      return;
    }
    if (!approve && !isOwnRequest && !this.canApprove) {
      this.toastService.showToaster(new Toaster({
        type: ToasterType.WARNING,
        message: `You do not have permission to reject ${this.changeLabel} changes`
      }));
      return;
    }
    const action = approve ? 'approve' : 'reject';
    const request = approve
      ? this.makerChecker.approve(row.id, remark, this.approvalsBase)
      : this.makerChecker.reject(row.id, remark, this.approvalsBase);
    this.loader.openLoading();
    request.toPromise()
      .then(() => {
        this.toastService.showToaster(new Toaster({
          type: ToasterType.SUCCESS,
          message: approve ? 'Change approved' : 'Change rejected'
        }));
        this.loader.closeLoading();
        this.fetchApprovals();
      })
      .catch((error) => {
        const body = error && error.error;
        this.toastService.showToaster(new Toaster({
          type: ToasterType.WARNING,
          message: (body && body.message) || `Could not ${action} request`
        }));
        this.loader.closeLoading();
      });
  }
}
